import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-hot-toast";
import { userService } from "@/services/user.service";

const CHANGE_PASSWORD_ERROR_FALLBACK =
	"Não foi possível alterar a senha. Tente novamente.";

function firstListString(value: unknown): string | undefined {
	return Array.isArray(value) && typeof value[0] === "string"
		? value[0]
		: undefined;
}

export function messageFromChangePasswordError(error: unknown): string {
	if (!axios.isAxiosError(error)) {
		return error instanceof Error
			? error.message
			: CHANGE_PASSWORD_ERROR_FALLBACK;
	}

	const data = error.response?.data;
	if (typeof data === "string" && data.trim()) {
		return data.trim();
	}
	if (data == null || typeof data !== "object" || Array.isArray(data)) {
		return CHANGE_PASSWORD_ERROR_FALLBACK;
	}

	const body = data as Record<string, unknown>;
	if (typeof body.detail === "string") {
		return body.detail;
	}
	if (typeof body.message === "string") {
		return body.message;
	}

	const nonField = firstListString(body.nonFieldErrors);
	if (nonField) {
		return nonField;
	}

	for (const value of Object.values(body)) {
		const fromField = firstListString(value);
		if (fromField) {
			return fromField;
		}
	}

	return CHANGE_PASSWORD_ERROR_FALLBACK;
}

export type ChangePasswordFieldErrors = {
	oldPassword?: string;
	newPassword?: string;
	confirmPassword?: string;
};

export function fieldErrorsFromChangePasswordError(
	error: unknown,
): ChangePasswordFieldErrors {
	if (!axios.isAxiosError(error)) {
		return {};
	}
	const data = error.response?.data;
	if (data == null || typeof data !== "object" || Array.isArray(data)) {
		return {};
	}
	const body = data as Record<string, unknown>;
	return {
		oldPassword: firstListString(body.oldPassword ?? body.old_password),
		newPassword: firstListString(body.newPassword1 ?? body.new_password1),
		confirmPassword: firstListString(body.newPassword2 ?? body.new_password2),
	};
}

export function useChangePassword() {
	return useMutation({
		mutationFn: (payload: {
			old_password: string;
			new_password1: string;
			new_password2: string;
		}) => userService().changePassword(payload),
		onSuccess: () => {
			toast.success("Senha alterada com sucesso!");
		},
		onError: (error) => {
			toast.error(messageFromChangePasswordError(error));
		},
	});
}
